/**
 * 발견사항 상세 표시 컴포넌트 (V1/V2 공통)
 * 수정/삭제/추가된 내용 표시, 되돌리기 기능 포함
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { Badge, TextButton } from '@safetyhub/ui';
import { AwardIcon, MapPinIcon, WrenchIcon, XIcon } from '../../icons';
import { getManagementActionLabel, getRiskAssessmentDisplay, getEvidenceDisplayName } from '@safetyhub/shared';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import { SeverityBadge } from './SeverityBadge';
import { EditableField } from './EditableField';
import { EditableMeasureList } from './EditableMeasureList';
import { EditableLegalList } from './EditableLegalList';
import { RiskAssessmentEditor } from './RiskAssessmentEditor';
import { ModifiedFieldHighlight, findModification, filterModificationsByConfig, calculateArrayChanges, ChangeStatusBadge } from './ModifiedFieldHighlight';
import { ConfirmDialog } from './ConfirmDialog';
import { HazardResolutionSection } from './HazardResolutionSection';
import { SearchableSelect } from './SearchableSelect';
import { AIAnalysisDetail, FindingSeverity, FindingModifications, BoundingBox, LawSearchStatus, SelectedMeasureEntry } from '../../../lib/inspections';
import { useInspectionHighlightStore } from '../../../stores/inspectionHighlight';
import { normalizeFileUrl } from '../../../lib/api';
import { getWorkCategoryDissent, resolveEffectiveWorkCategory } from '../../../lib/workCategory';
import { GroupedLegalBasisCardView, type LegalBasisItem } from '../../../utils/legalBasisFormatter';
import {
  getSiteLocations,
  getSiteDetailLocations,
  createSiteLocation,
  createSiteDetailLocation,
  type SiteLocation,
  type SiteDetailLocation,
} from '../../../lib/site-metadata';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';

interface Finding {
  id: string;
  category: string;
  subCategory?: string;
  description: string;
  severity: Severity;
  aiGenerated: boolean;
  aiConfidence?: number;
  aiAnalysisDetail?: AIAnalysisDetail;
  aiEdited?: boolean;
  modifications?: FindingModifications;
  lawSearchStatus?: LawSearchStatus;
  deletedAt?: string;
  deletedBy?: string;
  aiLikelihoodScore?: number | null;
  aiSeverityScore?: number | null;
  // TBM 분류 축 (규약 ③) — 분석 불변값 / 사람 확정값
  analyzedWorkCategory?: string | null;
  confirmedWorkCategory?: string | null;
  // 해소 관련 필드
  resolutionStatus?: 'PENDING' | 'COMPLIED' | 'NON_COMPLIED' | 'PARTIAL';
  resolutionPhotos?: Array<{ url: string; thumbnailUrl?: string; fileId?: string }>;
  resolutionPhotoUrl?: string;
  notes?: string;
  resolvedByUser?: { id: string; name: string };
  resolvedBySignerName?: string | null;
  /** 조치 시점 스냅샷 이름 (서버 신설 필드) */
  resolvedBySnapshotName?: string | null;
  resolvedAt?: string;
}

/** 발견사항 편집 핸들러 인터페이스 */
export interface FindingEditHandlers {
  onUpdateFinding: (findingId: string, data: {
    category?: string;
    subCategory?: string;
    description?: string;
    severity?: FindingSeverity;
    location?: { area?: string; detail?: string };
    riskAssessment?: { likelihood?: number; severity?: number };
    measures?: string[];
    legalBasis?: LegalBasisItem[];
    /** 위험 영역 좌표 (null이면 삭제) */
    boundingBox?: BoundingBox | null;
    /** 사람 확정 작업 분류 (null이면 확정 해제 → 사다리 폴백) */
    confirmedWorkCategory?: string | null;
  }, options?: { isRevert?: boolean }) => Promise<void>;
  onAddMeasure: (findingId: string, measure: string, measureId?: string) => Promise<void>;
  onRemoveMeasure: (findingId: string, index: number) => Promise<void>;
  onAddLegalBasis: (findingId: string, legalBasis: LegalBasisItem, selectedMeasures?: string[]) => Promise<void>;
  onRemoveLegalBasis: (findingId: string, index: number) => Promise<void>;
  onDeleteFinding?: (findingId: string) => Promise<void>;
  onRestoreFinding?: (findingId: string) => Promise<void>;
}

interface FindingDetailProps {
  finding: Finding;
  findingIndex: number;
  totalCount: number;
  globalFindingNumber?: number;
  editable?: boolean;
  editHandlers?: FindingEditHandlers;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: RiskAssessmentConfig | null;
  /** 컴팩트 모드 (V2 세로 레이아웃용) */
  compact?: boolean;
  /** 테이블 모드 (사진+발견사항 통합 테이블 내부) */
  tableMode?: boolean;
  /** 선택됨 (위험영역 강조용) */
  isSelected?: boolean;
  /** 클릭 핸들러 */
  onClick?: () => void;
  /** 위험영역 boundingBox 존재 여부 */
  hasBoundingBox?: boolean;
  /** 접기/펼치기 가능 여부 (기본: false) */
  collapsible?: boolean;
  /** 기본 펼침 상태 (기본: true) */
  defaultExpanded?: boolean;
  /** 위험영역 수정 여부 (위치/크기 변경 또는 삭제) */
  hasHazardEdit?: boolean;
  /** 위험영역 되돌리기 핸들러 */
  onHazardRevert?: () => void;
  /** 위험요인 해소 버튼 클릭 핸들러 */
  onResolveHazard?: (findingId: string) => void;
  /** 보고서 생성됨 여부 (해소 버튼 숨김) */
  hasReport?: boolean;
  /** 위험요인 해소 피처 플래그 활성화 여부 */
  hazardResolutionEnabled?: boolean;
  /** 발견사항 보고서 포함 선택 기능 활성화 여부 */
  findingSelectionEnabled?: boolean;
  /** 선택된 개선대책 목록 (선택자 정보 포함) */
  selectedMeasures?: SelectedMeasureEntry[];
  /** 개선대책 보고서 포함 여부 토글 핸들러 */
  onToggleMeasureSelection?: (measure: string, selected: boolean) => void;
  /** 조직 ID (장소/위치 SearchableSelect용) */
  companyId?: string;
  /** 현장 ID (장소/위치 SearchableSelect용) */
  siteId?: string;
  /** 현재 로그인 사용자 ID (본인 선택 해제 권한 판별용) */
  currentUserId?: string;
  /** 현재 사용자가 completion 서명을 마쳤는지 (완료 후 해제 불가) */
  isMyCompleted?: boolean;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (override 권한) */
  isPrimary?: boolean;
  /** 현재 사용자가 조직 Owner인지 (override 권한) */
  isOwner?: boolean;
  /** 사진에 직접 지정된 공종/공정 (작업 분류 사다리 2층) */
  photoProcessType?: string | null;
  /** 사진 대상(설비)에 지정된 공종/공정 (같은 2층의 폴백) */
  equipmentProcessType?: string | null;
}

/**
 * 필드별 하이라이트 래퍼 컴포넌트
 */
function FieldHighlight({
  findingId,
  field,
  children,
  className = '',
}: {
  findingId: string;
  field: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { isFieldHighlighted, removeFieldHighlight, getHighlightInfo } = useInspectionHighlightStore();
  const highlighted = isFieldHighlighted(findingId, field);
  const highlightInfo = getHighlightInfo(findingId);

  if (!highlighted) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={`relative ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        removeFieldHighlight(findingId, field);
      }}
    >
      {/* 하이라이트 배경 */}
      <div className="absolute inset-0 -m-1 bg-yellow-200 rounded animate-pulse pointer-events-none" />
      {/* 수정됨 뱃지 */}
      <div className="absolute -top-2 -right-1 z-10">
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-yellow-400 text-yellow-900 rounded-full shadow-sm">
          {highlightInfo?.updatedBy ? `${highlightInfo.updatedBy}` : '수정됨'}
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeFieldHighlight(findingId, field);
            }}
            className="ml-0.5 hover:text-yellow-700"
            aria-label="강조 표시 제거"
          >
            <XIcon className="h-3 w-3" />
          </button>
        </span>
      </div>
      {/* 내용 */}
      <div className="relative">{children}</div>
    </div>
  );
}

/** hex color → Tailwind 클래스 (SeverityBadge 패턴 재사용) */
function hexColorToRiskStyle(color?: string): string {
  if (!color) return 'bg-ink-100 text-ink-800 border-line';
  const c = color.toUpperCase();
  if (c === '#EF4444' || c === '#DC2626' || c === '#B91C1C') return 'bg-red-100 text-red-800 border-red-200';
  if (c === '#F97316' || c === '#EA580C') return 'bg-orange-100 text-orange-800 border-orange-200';
  if (c === '#EAB308' || c === '#CA8A04' || c === '#F59E0B' || c === '#D97706') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (c === '#22C55E' || c === '#16A34A' || c === '#15803D') return 'bg-green-100 text-green-800 border-green-200';
  if (c === '#3B82F6' || c === '#2563EB') return 'bg-blue-100 text-blue-800 border-blue-200';
  return 'bg-ink-100 text-ink-800 border-line';
}

/** config 없을 때 fallback: 3x3(1~9), 5x5(1~25) 공용 스타일 */
function getRiskLevelStyleFallback(riskLevel: number): string {
  if (riskLevel >= 15) return 'bg-red-100 text-red-800 border-red-200';
  if (riskLevel >= 9) return 'bg-orange-100 text-orange-800 border-orange-200';
  if (riskLevel >= 4) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-green-100 text-green-800 border-green-200';
}


/**
 * AI 신뢰도에 따른 스타일
 */
function getConfidenceStyle(confidence: number): string {
  if (confidence >= 0.8) return 'text-green-600';
  if (confidence >= 0.5) return 'text-yellow-600';
  return 'text-red-600';
}

// 필드별 라벨
const fieldLabels: Record<string, string> = {
  'category': '카테고리',
  'subCategory': '서브카테고리',
  'description': '설명',
  'severity': '심각도',
  'location': '위치',
  'riskAssessment': '위험성 평가',
  'riskAssessment.likelihood': '빈도',
  'riskAssessment.severity': '강도',
  'riskAssessment.riskLevel': '위험도',
  'measures': '개선대책',
  'legalBasis': '관련법규',
  'boundingBox': '위험영역',
};

/**
 * 발견사항 상세 표시 컴포넌트
 */
export function FindingDetail({
  finding,
  findingIndex,
  totalCount,
  globalFindingNumber,
  editable = false,
  editHandlers,
  compact = false,
  tableMode = false,
  isSelected = false,
  onClick,
  hasBoundingBox = false,
  collapsible = false,
  defaultExpanded = true,
  hasHazardEdit = false,
  onHazardRevert,
  onResolveHazard,
  hasReport = false,
  hazardResolutionEnabled = false,
  findingSelectionEnabled = false,
  selectedMeasures,
  onToggleMeasureSelection,
  companyId,
  siteId,
  currentUserId,
  isMyCompleted = false,
  isPrimary = false,
  isOwner = false,
  riskConfig,
  photoProcessType,
  equipmentProcessType,
}: FindingDetailProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const deviceLayout = useDeviceLayout();
  const isDesktop = deviceLayout === 'desktop';
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isSavingWorkCategory, setIsSavingWorkCategory] = useState(false);
  const detail = finding.aiAnalysisDetail;

  // riskLevel 계산 + config 기반 color 보강
  const baseRiskAssessment = detail?.riskAssessment;
  const riskAssessment = (() => {
    if (!baseRiskAssessment) return undefined;

    const { likelihood, severity } = baseRiskAssessment;

    if (riskConfig && likelihood && severity) {
      const display = getRiskAssessmentDisplay({
        likelihood,
        severity,
        riskLevel: baseRiskAssessment.riskLevel,
        config: riskConfig,
      });
      if (display) {
        return {
          ...baseRiskAssessment,
          riskColor: display.color,
          action: display.managementAction ?? baseRiskAssessment.action,
        };
      }
      return baseRiskAssessment;
    }

    // config 없을 때 fallback — score 그대로 유지
    return baseRiskAssessment;
  })();

  const recommendation = detail?.recommendation;
  const location = detail?.location;

  // 장소/위치 목록 (canEdit + companyId + siteId가 모두 있을 때만 조회)
  const locationQueryEnabled = !!(editable && editHandlers && !finding.deletedAt && companyId && siteId);
  const { data: siteLocations = [] } = useQuery<SiteLocation[]>({
    queryKey: ['site-locations', companyId, siteId],
    queryFn: () => getSiteLocations(companyId!, siteId!),
    enabled: locationQueryEnabled,
    staleTime: 5 * 60 * 1000,
  });
  const { data: siteDetailLocations = [], refetch: refetchDetailLocations } = useQuery<SiteDetailLocation[]>({
    queryKey: ['site-detail-locations', companyId, siteId],
    queryFn: () => getSiteDetailLocations(companyId!, siteId!),
    enabled: locationQueryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const activeLocations = siteLocations.filter((l) => l.isActive);
  const activeDetailLocations = siteDetailLocations.filter((l) => l.isActive);

  // 우수사례 여부 (severity가 GOOD이면 우수사례 발견사항)
  const isBestPractice = finding.severity === 'GOOD';

  // 우수사례 보고서 포함 상태 (null/undefined → 기본 포함, ['__bp_included__'] → 포함, [] → 제외)
  const BP_INCLUDED_MARKER = '__bp_included__';
  const bpIncluded = !Array.isArray(selectedMeasures) || selectedMeasures.length > 0;
  const bpExcludedFromReport = isBestPractice && findingSelectionEnabled && hasReport && !bpIncluded;

  // 삭제 여부 확인
  const isDeleted = !!finding.deletedAt;

  // 편집 가능 여부 (삭제된 경우 편집 불가)
  const canEdit = editable && editHandlers && !isDeleted;

  // 실시간 변경 하이라이트 상태 확인
  const { isHighlighted, getHighlightInfo, removeHighlight } = useInspectionHighlightStore();
  const highlighted = isHighlighted(finding.id);
  const highlightInfo = getHighlightInfo(finding.id);

  // AI 수정 정보
  const aiModifications = finding.modifications;
  const visibleModifiedFields = filterModificationsByConfig(aiModifications?.modifiedFields, riskConfig);
  const hasAiModifications = aiModifications?.isModified && visibleModifiedFields.length > 0;

  // 클릭 핸들러 (버튼 클릭 제외)
  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-action-button]')) return;
    if ((e.target as HTMLElement).closest('[data-toggle-expand]')) return;
    if ((e.target as HTMLElement).closest('button')) return;
    if ((e.target as HTMLElement).closest('input')) return;
    onClick?.();
  };

  // --- 공통 서브 렌더러 ---
  /** 배너 렌더러 (삭제됨/수동추가/실시간변경/AI수정) */
  const renderBanners = (fullWidth = false) => {
    const wrapCls = fullWidth ? 'col-span-full' : '';
    return (
      <>
        {/* 삭제됨 표시 (soft delete) */}
        {isDeleted && (
          <div className={`${wrapCls} text-xs text-ink-500 bg-ink-100 border border-line px-2.5 py-1.5 flex items-center gap-2`}>
            <span className="font-medium">삭제됨</span>
            {editable && editHandlers?.onRestoreFinding && (
              <button
                data-action-button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsRestoring(true);
                  try {
                    await editHandlers.onRestoreFinding?.(finding.id);
                  } catch (error) {
                    // ⚠️ **삼키는 것이 아니다** — 사용자 안내의 소유자는 호출부다.
                    // `useInspectionDetailPage`의 `handleRestoreFinding`이 `setError(apiError.message)`로
                    // 페이지 에러 토스트(`pages/inspections/[id].adaptive.tsx`, `role="alert"`)에
                    // 서버 문구를 싣고 **그 뒤에** rethrow한다. 여기서 또 모달을 띄우면 같은 사유가
                    // 두 번 뜬다. 이 `console.error`를 「안내 누락」으로 읽고 고치지 말 것.
                    console.error('발견사항 복원 실패:', error);
                  } finally {
                    setIsRestoring(false);
                  }
                }}
                disabled={isRestoring}
                className="ml-auto px-2 py-0.5 text-xs font-medium text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded transition-colors disabled:opacity-50"
              >
                {isRestoring ? '복원 중...' : '되돌리기'}
              </button>
            )}
          </div>
        )}
        {/* 수동 추가됨 표시 */}
        {!isDeleted && !finding.aiGenerated && (
          <div className={`${wrapCls} text-xs text-ink-500 bg-ink-100 border border-line px-2.5 py-1.5 flex items-center gap-2`}>
            <span className="font-medium">수동 추가됨</span>
            {editable && editHandlers?.onDeleteFinding && (
              <button
                data-action-button
                onClick={(e) => { e.stopPropagation(); setShowRevertConfirm(true); }}
                disabled={isDeleting}
                className="ml-auto px-2 py-0.5 text-xs font-medium text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded transition-colors disabled:opacity-50"
              >
                {isDeleting ? '삭제 중...' : '되돌리기'}
              </button>
            )}
          </div>
        )}
        {/* 실시간 변경 알림 */}
        {!isDeleted && highlighted && highlightInfo && highlightInfo.fields.length > 0 && (
          <div className={`${wrapCls} text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 px-2.5 py-1.5 flex items-center gap-2`}>
            <span className="font-medium">{highlightInfo.updatedBy || '다른 사용자'}님이 수정</span>
            <span className="text-yellow-600">({highlightInfo.fields.map(f => fieldLabels[f] || f).join(', ')})</span>
            <button data-action-button onClick={(e) => { e.stopPropagation(); removeHighlight(finding.id); }} className="ml-auto text-yellow-600 hover:text-yellow-800 font-medium">확인</button>
          </div>
        )}
        {/* AI 수정 배너 */}
        {!isDeleted && hasAiModifications && (
          <div className={`${wrapCls} text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 flex items-center gap-2`}>
            <span className="font-medium">분석에서 수정됨</span>
            <span className="text-amber-600">({visibleModifiedFields.map(m => fieldLabels[m.field] || m.field).join(', ')})</span>
          </div>
        )}
      </>
    );
  };

  /** 확인 모달 렌더러 */
  const renderConfirmDialogs = () => (
    <>
      <ConfirmDialog
        isOpen={showRevertConfirm}
        onClose={() => setShowRevertConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await editHandlers?.onDeleteFinding?.(finding.id);
            setShowRevertConfirm(false);
          } catch (error) {
            console.error('발견사항 삭제 실패:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        title={isBestPractice ? '우수사례 되돌리기' : '발견사항 되돌리기'}
        message={isBestPractice ? '수동 추가한 우수사례를 삭제하시겠습니까?' : '수동 추가한 발견사항을 삭제하시겠습니까?'}
        confirmText="삭제"
        isLoading={isDeleting}
      />
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await editHandlers?.onDeleteFinding?.(finding.id);
            setShowDeleteConfirm(false);
          } catch (error) {
            console.error('발견사항 삭제 실패:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        title={isBestPractice ? '우수사례 삭제' : '발견사항 삭제'}
        message={isBestPractice ? '이 우수사례를 삭제하시겠습니까?' : '이 발견사항을 삭제하시겠습니까?'}
        confirmText="삭제"
        isLoading={isDeleting}
      />
    </>
  );

  /** 삭제 버튼 렌더러 */
  const renderDeleteButton = () => {
    if (!canEdit || !editHandlers?.onDeleteFinding) return null;
    // AI 생성 발견사항 또는 우수사례만 삭제 가능
    if (!finding.aiGenerated && !isBestPractice) return null;
    return (
      <button
        data-action-button
        onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }}
        disabled={isDeleting}
        className="p-1 text-ink-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
        title="발견사항 삭제"
      >
        {isDeleting ? (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        )}
      </button>
    );
  };

  /** 위험영역 되돌리기 버튼 렌더러 */
  const renderHazardRevertButton = () => {
    if (!hasHazardEdit || !onHazardRevert) return null;
    return (
      <button
        data-action-button
        onClick={(e) => { e.stopPropagation(); onHazardRevert(); }}
        className="flex items-center gap-1 px-2 py-0.5 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded transition-colors"
        title="위험영역 되돌리기"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
        <span>위험영역</span>
      </button>
    );
  };

  /** 접기/펼치기 버튼 렌더러 */
  const renderCollapseButton = () => {
    if (!collapsible) return null;
    return (
      <button
        data-toggle-expand
        onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
        className="ml-auto p-1 text-ink-400 hover:text-ink-600 rounded hover:bg-surface-hover transition-colors"
        title={isExpanded ? '접기' : '펼치기'}
      >
        <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  };

  /** 카테고리 수정 정보 */
  const categoryMod = findModification(aiModifications?.modifiedFields, 'category');
  const subCategoryMod = findModification(aiModifications?.modifiedFields, 'subCategory');
  const hasCategoryMod = categoryMod || subCategoryMod;
  const combinedCategoryMod = hasCategoryMod ? {
    field: 'category',
    originalValue: categoryMod ? categoryMod.originalValue : finding.category,
    currentValue: categoryMod ? categoryMod.currentValue : finding.category,
  } : undefined;
  const categoryRevertHandler = canEdit && hasCategoryMod ? async () => {
    const updates: { category?: string; subCategory?: string } = {};
    if (categoryMod) updates.category = categoryMod.originalValue as string;
    if (subCategoryMod) {
      const origSubCat = subCategoryMod.originalValue as string | null;
      updates.subCategory = origSubCat ?? undefined;
    }
    if (editHandlers && Object.keys(updates).length > 0) {
      await editHandlers.onUpdateFinding(finding.id, updates, { isRevert: true });
    }
  } : undefined;
  const categoryFormatOriginal = hasCategoryMod ? () => {
    const origCategory = categoryMod ? (categoryMod.originalValue as string) : finding.category;
    const origSubCategory = subCategoryMod ? (subCategoryMod.originalValue as string | null) : finding.subCategory;
    return origCategory + (origSubCategory ? ` > ${origSubCategory}` : '');
  } : undefined;

  /** 개선대책/관련법규 수정 여부 */
  const measuresMod = findModification(aiModifications?.modifiedFields, 'measures');
  const legalBasisMod = findModification(aiModifications?.modifiedFields, 'legalBasis');

  /** 카테고리 표시 컨텐츠 렌더러 */
  const renderCategoryContent = () => {
    if (canEdit) {
      return (
        <EditableField
          value={finding.category + (finding.subCategory ? ` > ${finding.subCategory}` : '')}
          onSave={async (value) => {
            const parts = value.split('>').map(s => s.trim());
            await editHandlers!.onUpdateFinding(finding.id, {
              category: parts[0],
              subCategory: parts[1] || undefined,
            });
          }}
          className="text-sm font-medium text-ink-800"
        />
      );
    }
    return (
      <span className={`text-sm font-medium text-ink-800 ${isDeleted ? 'line-through' : ''}`}>
        {finding.category}
        {finding.subCategory && <span className="text-ink-500"> &gt; {finding.subCategory}</span>}
      </span>
    );
  };

  /**
   * 작업 분류 — 유효값 사다리와 이견 판정 (규약 ③④).
   * 사용자에게 보이는 어휘는 「분석 결과」다 — "AI"를 직접 노출하지 않는다(규약 ⑤).
   */
  const workCategorySources = {
    confirmedWorkCategory: finding.confirmedWorkCategory,
    photoProcessType,
    equipmentProcessType,
    analyzedWorkCategory: finding.analyzedWorkCategory,
  };
  const effectiveWorkCategory = resolveEffectiveWorkCategory(workCategorySources);
  const workCategoryDissent = getWorkCategoryDissent(workCategorySources);
  const hasConfirmedWorkCategory = !!finding.confirmedWorkCategory?.trim();

  /** 확정값 쓰기 — 적용은 분석값의 복사다(참조 아님, 규약 ④). null이면 확정 해제. */
  const saveWorkCategory = async (value: string | null) => {
    if (!editHandlers || isSavingWorkCategory) return;
    setIsSavingWorkCategory(true);
    try {
      await editHandlers.onUpdateFinding(finding.id, { confirmedWorkCategory: value });
    } finally {
      setIsSavingWorkCategory(false);
    }
  };

  /** 작업 분류 행 렌더러 — 유효값이 아무 층에도 없으면 행 자체를 두지 않는다 */
  const renderWorkCategoryRow = (rowClass: string, labelClass: string, valueClass: string) => {
    if (!effectiveWorkCategory) return null;
    return (
      <div className={rowClass}>
        <div className={labelClass}>작업 분류</div>
        <div className={`${valueClass} flex items-center gap-2 flex-wrap`}>
          <span className={`text-sm font-medium text-ink-800 ${isDeleted ? 'line-through' : ''}`}>
            {effectiveWorkCategory}
          </span>
          {workCategoryDissent && (
            <Badge tone="amber" shape="rounded" size="sm">
              분석 결과: {workCategoryDissent.analyzed}
            </Badge>
          )}
          {canEdit && workCategoryDissent && (
            <TextButton
              className="text-xs font-medium"
              disabled={isSavingWorkCategory}
              onClick={() => saveWorkCategory(workCategoryDissent.analyzed)}
            >
              분석 결과 적용
            </TextButton>
          )}
          {canEdit && hasConfirmedWorkCategory && (
            <TextButton
              tone="neutral"
              className="text-xs"
              disabled={isSavingWorkCategory}
              onClick={() => saveWorkCategory(null)}
            >
              확정 해제
            </TextButton>
          )}
        </div>
      </div>
    );
  };

  /** 위치 행 렌더러 */
  const renderLocationContent = () => {
    if (!((location && (location.area || location.detail)) || canEdit)) return null;

    // 텍스트 값으로 현재 장소/위치 ID 매칭
    const matchedLocationId = activeLocations.find((l) => l.name === location?.area)?.id ?? '';
    const matchedDetailLocationId = activeDetailLocations.find((l) => l.name === location?.detail)?.id ?? '';

    // SearchableSelect 사용 가능 여부 (companyId + siteId가 있을 때)
    const useLocationSelect = locationQueryEnabled;

    return (
      <FieldHighlight findingId={finding.id} field="location">
        <ModifiedFieldHighlight
          modification={findModification(aiModifications?.modifiedFields, 'location')}
          editable={!!canEdit}
          onRevert={canEdit ? async () => {
            const mod = findModification(aiModifications?.modifiedFields, 'location');
            if (mod && editHandlers) {
              const original = mod.originalValue as { area?: string; detail?: string };
              await editHandlers.onUpdateFinding(finding.id, { location: original }, { isRevert: true });
            }
          } : undefined}
        >
          {canEdit ? (
            useLocationSelect ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-ink-500 flex-shrink-0">장소</span>
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    label="장소"
                    value={matchedLocationId}
                    onChange={async (v) => {
                      const loc = activeLocations.find((l) => l.id === v);
                      await editHandlers!.onUpdateFinding(finding.id, {
                        location: { ...location, area: loc?.name || undefined },
                      });
                    }}
                    options={activeLocations}
                    placeholder="장소 선택"
                    onAdd={async (name) => {
                      const created = await createSiteLocation(companyId!, siteId!, { name });
                      return created.id;
                    }}
                    sheetMode={!isDesktop}
                  />
                </div>
                <span className="text-xs font-medium text-ink-500 flex-shrink-0">상세위치</span>
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    label="상세위치"
                    value={matchedDetailLocationId}
                    onChange={async (v) => {
                      const dl = activeDetailLocations.find((l) => l.id === v);
                      await editHandlers!.onUpdateFinding(finding.id, {
                        location: { ...location, detail: dl?.name || undefined },
                      });
                    }}
                    options={activeDetailLocations}
                    placeholder="상세위치 선택"
                    onAdd={async (name) => {
                      const created = await createSiteDetailLocation(companyId!, siteId!, { name });
                      await refetchDetailLocations();
                      return created.id;
                    }}
                    sheetMode={!isDesktop}
                  />
                </div>
              </div>
            ) : (
              <EditableField
                value={[location?.area, location?.detail].filter(Boolean).join(' - ')}
                onSave={async (value) => {
                  const parts = value.split('-').map(s => s.trim());
                  await editHandlers!.onUpdateFinding(finding.id, {
                    location: { area: parts[0] || undefined, detail: parts[1] || undefined },
                  });
                }}
                placeholder="위치 정보 추가"
              />
            )
          ) : (
            <span>{location?.area}{location?.area && location?.detail && ' - '}{location?.detail}</span>
          )}
        </ModifiedFieldHighlight>
      </FieldHighlight>
    );
  };

  /** 설명 렌더러 */
  const renderDescriptionContent = () => (
    <FieldHighlight findingId={finding.id} field="description">
      <ModifiedFieldHighlight
        modification={findModification(aiModifications?.modifiedFields, 'description')}
        editable={!!canEdit}
        onRevert={canEdit ? async () => {
          const mod = findModification(aiModifications?.modifiedFields, 'description');
          if (mod && editHandlers) {
            const original = mod.originalValue as string;
            await editHandlers.onUpdateFinding(finding.id, { description: original }, { isRevert: true });
          }
        } : undefined}
      >
        {canEdit ? (
          <EditableField
            value={finding.description}
            onSave={async (value) => { await editHandlers!.onUpdateFinding(finding.id, { description: value }); }}
            type="textarea"
            className="text-sm text-ink-700 leading-relaxed"
          />
        ) : (
          <p className="text-sm text-ink-700 leading-relaxed">{finding.description}</p>
        )}
      </ModifiedFieldHighlight>
    </FieldHighlight>
  );

  /** 위험성 평가 렌더러 */
  const renderRiskAssessment = () => {
    if (!riskAssessment) return null;
    return (
      <FieldHighlight findingId={finding.id} field="riskAssessment">
        <div className="flex flex-wrap gap-2 text-xs">
          <RiskAssessmentEditor
            riskAssessment={{
              ...riskAssessment,
              riskColor: (riskAssessment as any).riskColor,
            }}
            config={riskConfig}
            canEdit={!!canEdit}
            onSave={async (updated) => {
              await editHandlers!.onUpdateFinding(finding.id, { riskAssessment: updated });
            }}
            getRiskStyle={(level, color) => color ? hexColorToRiskStyle(color) : getRiskLevelStyleFallback(level)}
            aiModifications={aiModifications}
            onRevert={canEdit ? async (field, original) => {
              await editHandlers!.onUpdateFinding(finding.id, {
                riskAssessment: { [field]: original },
              }, { isRevert: true });
            } : undefined}
          />
        </div>
        {riskAssessment.action && (
          <div className="mt-1.5 text-xs text-ink-600"><span className="text-ink-500">필요 조치:</span> {getManagementActionLabel(riskAssessment.action)}</div>
        )}
      </FieldHighlight>
    );
  };

  /** 개선대책 렌더러 */
  const renderMeasures = (hideLabel = false) => (
    <FieldHighlight findingId={finding.id} field="measures">
      {canEdit ? (
        <MeasuresWithChanges
          currentMeasures={recommendation?.measures || []}
          measureItems={recommendation?.measureItems}
          modification={findModification(aiModifications?.modifiedFields, 'measures')}
          hkMeasureCount={detail?.hkMeasureCount}
          editable={true}
          hideLabel={hideLabel}
          onRevert={async () => {
            const mod = findModification(aiModifications?.modifiedFields, 'measures');
            if (mod && editHandlers) { await editHandlers.onUpdateFinding(finding.id, { measures: mod.originalValue as string[] }, { isRevert: true }); }
          }}
          onAdd={async (measure: string, measureId?: string) => { await editHandlers!.onAddMeasure(finding.id, measure, measureId); }}
          onRemove={async (index) => { await editHandlers!.onRemoveMeasure(finding.id, index); }}
          findingSelectionEnabled={findingSelectionEnabled}
          selectedMeasures={selectedMeasures}
          onToggleMeasureSelection={onToggleMeasureSelection}
          hasReport={hasReport}
          currentUserId={currentUserId}
          isMyCompleted={isMyCompleted}
          isPrimary={isPrimary}
          isOwner={isOwner}
          isDeleted={isDeleted}
        />
      ) : (
        <MeasuresWithChanges
          currentMeasures={recommendation?.measures || []}
          measureItems={recommendation?.measureItems}
          modification={findModification(aiModifications?.modifiedFields, 'measures')}
          hkMeasureCount={detail?.hkMeasureCount}
          hideLabel={hideLabel}
          findingSelectionEnabled={findingSelectionEnabled}
          selectedMeasures={selectedMeasures}
          onToggleMeasureSelection={onToggleMeasureSelection}
          hasReport={hasReport}
          currentUserId={currentUserId}
          isMyCompleted={isMyCompleted}
          isPrimary={isPrimary}
          isOwner={isOwner}
          isDeleted={isDeleted}
        />
      )}
    </FieldHighlight>
  );

  /** 관련법규 렌더러 */
  const renderLegalBasisItem = (hideLabel = false) => (
    <FieldHighlight findingId={finding.id} field="legalBasis">
      {canEdit ? (
        <LegalBasisItemWithChanges
          currentLegalBasis={recommendation?.legalBasis || []}
          modification={findModification(aiModifications?.modifiedFields, 'legalBasis')}
          editable={true}
          hideLabel={hideLabel}
          onRevert={async () => {
            const mod = findModification(aiModifications?.modifiedFields, 'legalBasis');
            if (mod && editHandlers) { await editHandlers.onUpdateFinding(finding.id, { legalBasis: mod.originalValue as LegalBasisItem[] }, { isRevert: true }); }
          }}
          onAdd={async (legalBasis, selectedMeasures) => { await editHandlers!.onAddLegalBasis(finding.id, legalBasis, selectedMeasures); }}
          onRemove={async (index) => { await editHandlers!.onRemoveLegalBasis(finding.id, index); }}
          lawSearchStatus={finding.lawSearchStatus}
          category={finding.category}
          subCategory={finding.subCategory}
          measures={recommendation?.measures || []}
        />
      ) : (
        <LegalBasisItemWithChanges
          currentLegalBasis={recommendation?.legalBasis || []}
          modification={findModification(aiModifications?.modifiedFields, 'legalBasis')}
          hideLabel={hideLabel}
          lawSearchStatus={finding.lawSearchStatus}
          category={finding.category}
          subCategory={finding.subCategory}
          measures={recommendation?.measures || []}
        />
      )}
    </FieldHighlight>
  );

  /** 해소 섹션 렌더러 */
  const renderResolution = () => (
    <>
      {hazardResolutionEnabled && finding.resolutionStatus === 'COMPLIED' && (finding.notes || finding.resolutionPhotos?.length || finding.resolutionPhotoUrl) && (
        <HazardResolutionSection
          resolutionPhotos={finding.resolutionPhotos}
          resolutionPhotoUrl={finding.resolutionPhotoUrl}
          notes={finding.notes}
          resolvedByName={getEvidenceDisplayName(finding.resolvedBySnapshotName ?? finding.resolvedBySignerName ?? null, finding.resolvedByUser ?? null)}
          resolvedAt={finding.resolvedAt}
          editable={editable && !hasReport}
          onEdit={onResolveHazard ? () => onResolveHazard(finding.id) : undefined}
        />
      )}
      {hazardResolutionEnabled && finding.resolutionStatus === 'PENDING' && editable && !hasReport && !isDeleted && onResolveHazard && (
        <button
          type="button"
          data-action-button
          onClick={(e) => { e.stopPropagation(); onResolveHazard(finding.id); }}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-control hover:bg-amber-100 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-8-8h16" />
          </svg>
          위험요인 해소
        </button>
      )}
    </>
  );

  // =====================================================================
  // tableMode: 테이블 행 레이아웃 (사진+발견사항 통합 테이블 내부)
  // =====================================================================
  if (tableMode) {
    const labelBase = 'bg-ink-100 px-2.5 py-1.5 font-medium text-ink-600 border-r border-ink-300 text-xs';
    const labelCls = `${labelBase} whitespace-nowrap`;
    const valueCls = 'px-2.5 py-1.5 text-sm';
    const rowBorder = 'border-b border-ink-300';

    // ── 우수사례 전용 테이블 레이아웃 ──
    if (isBestPractice) {
      return (
        <div className={`${findingIndex > 0 ? 'border-t-2 border-emerald-200' : ''} ${isDeleted ? 'opacity-60' : ''} ${bpExcludedFromReport ? 'opacity-60' : ''}`}>
          {/* 배너 */}
          {renderBanners(true)}

          {/* 보고서 포함 안내 - 첫 항목 위에만 */}
          {findingIndex === 0 && findingSelectionEnabled && !hasReport && onToggleMeasureSelection && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-600">
              보고서에 포함할 우수사례를 선택해주세요 (미선택 시 보고서에서 제외됩니다)
            </div>
          )}

          {/* No + 우수 뱃지 */}
          <div className={`grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] ${rowBorder}`}>
            <div className={labelCls}>No</div>
            <div className={`${valueCls} flex items-center gap-2 flex-wrap`}>
              {findingSelectionEnabled && !hasReport && onToggleMeasureSelection && (
                <input
                  type="checkbox"
                  checked={bpIncluded}
                  onChange={(e) => onToggleMeasureSelection(BP_INCLUDED_MARKER, e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
              )}
              {findingSelectionEnabled && hasReport && (
                bpIncluded
                  ? <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  : <svg className="w-3.5 h-3.5 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" /></svg>
              )}
              {globalFindingNumber !== undefined && <span className="font-medium">{globalFindingNumber}</span>}
              <SeverityBadge severity="GOOD" size="sm" />
              {finding.aiGenerated && finding.aiConfidence !== undefined && (
                <span className={`text-[10px] ${getConfidenceStyle(finding.aiConfidence)}`}>{Math.round(finding.aiConfidence * 100)}%</span>
              )}
              {renderDeleteButton()}
              {renderCollapseButton()}
            </div>
          </div>

          {(!collapsible || isExpanded) && (
            <>
              {/* 분류 */}
              <div className={`grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] ${rowBorder}`}>
                <div className={labelCls}>분류</div>
                <div className={valueCls}>
                  <FieldHighlight findingId={finding.id} field="category">
                    <ModifiedFieldHighlight modification={combinedCategoryMod} editable={!!canEdit} onRevert={categoryRevertHandler} formatOriginal={categoryFormatOriginal}>
                      {renderCategoryContent()}
                    </ModifiedFieldHighlight>
                  </FieldHighlight>
                </div>
              </div>

              {/* 내용 (description) */}
              <div className={rowBorder}>
                <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr]">
                  <div className={`${labelCls} sm:row-span-1`}>내용</div>
                  <div className={`${valueCls} hidden sm:block`}>{renderDescriptionContent()}</div>
                </div>
                <div className="sm:hidden px-2.5 py-1.5">{renderDescriptionContent()}</div>
              </div>

              {/* 우수 포인트 (measures가 있을 때만) */}
              {recommendation?.measures && recommendation.measures.length > 0 && (
                <div className={rowBorder}>
                  <div className="hidden sm:grid grid-cols-[6rem_1fr]">
                    <div className={labelBase}>우수 포인트</div>
                    <div className={valueCls}>
                      <ul className="text-sm text-ink-700 space-y-1 ml-4">
                        {recommendation.measures.map((m, i) => (
                          <li key={i} className="list-disc list-outside">{m}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="bg-ink-100 px-2.5 py-1 font-medium text-ink-600 text-xs border-b border-line">우수 포인트</div>
                    <div className="px-2.5 py-1.5">
                      <ul className="text-sm text-ink-700 space-y-1 ml-4">
                        {recommendation.measures.map((m, i) => (
                          <li key={i} className="list-disc list-outside">{m}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {renderConfirmDialogs()}
        </div>
      );
    }

    // 선택된 대책이 없는 경우 finding 전체를 흐리게 표시 (보고서 생성 전/후 모두)
    const allMeasuresExcluded = findingSelectionEnabled && (!selectedMeasures || selectedMeasures.length === 0);
    const excludedFromReport = hasReport && allMeasuresExcluded;

    // ── 기존 위험요인 테이블 레이아웃 ──
    return (
      <div
        className={`${findingIndex > 0 ? 'border-t-2 border-ink-300' : ''} ${isDeleted ? 'opacity-60' : ''} ${isSelected ? 'bg-brand-50' : ''} ${excludedFromReport ? 'opacity-60 text-ink-400' : allMeasuresExcluded ? 'text-ink-400' : ''}`}
        onClick={handleClick}
      >
        {/* 배너 (삭제됨/수동추가/실시간변경/AI수정) */}
        {renderBanners(true)}

        {/* 보고서 미포함 배너 */}
        {excludedFromReport && (
          <div className="bg-ink-100 border-b border-line px-3 py-1.5 text-xs text-ink-500 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" /></svg>
            보고서에서 제외된 발견사항
          </div>
        )}

        {/* 1행: No (Desktop) */}
        <div className={`hidden sm:grid grid-cols-[6rem_1fr] ${rowBorder}`}>
          <div className={labelCls}>No</div>
          <div className={`${valueCls} flex items-center gap-2 flex-wrap`}>
            {globalFindingNumber !== undefined && <span className="font-medium">{globalFindingNumber}</span>}
            <SeverityBadge
              likelihoodScore={riskAssessment?.likelihood ?? finding.aiLikelihoodScore}
              severityScore={riskAssessment?.severity ?? finding.aiSeverityScore}
              riskLevelScore={riskAssessment?.riskLevel ?? undefined}
              config={riskConfig}
              size="sm"
            />
            {hazardResolutionEnabled && finding.resolutionStatus === 'COMPLIED' && (
              <Badge tone="emerald" shape="rounded" size="sm">해소됨</Badge>
            )}
            {finding.aiGenerated && finding.aiConfidence !== undefined && (
              <span className={`text-[10px] ${getConfidenceStyle(finding.aiConfidence)}`}>{Math.round(finding.aiConfidence * 100)}%</span>
            )}
            {renderDeleteButton()}
            {renderHazardRevertButton()}
            {renderCollapseButton()}
          </div>
        </div>
        {/* Mobile: No */}
        <div className={`sm:hidden grid grid-cols-[4.5rem_1fr] ${rowBorder}`}>
          <div className={labelCls}>No</div>
          <div className={`${valueCls} flex items-center gap-2 flex-wrap`}>
            {globalFindingNumber !== undefined && <span className="font-medium">{globalFindingNumber}</span>}
            <SeverityBadge
              likelihoodScore={riskAssessment?.likelihood ?? finding.aiLikelihoodScore}
              severityScore={riskAssessment?.severity ?? finding.aiSeverityScore}
              riskLevelScore={riskAssessment?.riskLevel ?? undefined}
              config={riskConfig}
              size="sm"
            />
            {hazardResolutionEnabled && finding.resolutionStatus === 'COMPLIED' && (
              <Badge tone="emerald" shape="rounded" size="sm">해소됨</Badge>
            )}
            {renderDeleteButton()}
            {renderHazardRevertButton()}
            {renderCollapseButton()}
          </div>
        </div>

        {/* 상세 내용 (접기 가능) */}
        {(!collapsible || isExpanded) && (
          <>
            {/* 위험요인 (카테고리) */}
            <div className={`grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] ${rowBorder}`}>
              <div className={labelCls}>위험요인</div>
              <div className={valueCls}>
                <FieldHighlight findingId={finding.id} field="category">
                  <ModifiedFieldHighlight modification={combinedCategoryMod} editable={!!canEdit} onRevert={categoryRevertHandler} formatOriginal={categoryFormatOriginal}>
                    {renderCategoryContent()}
                  </ModifiedFieldHighlight>
                </FieldHighlight>
              </div>
            </div>

            {/* 작업 분류 — 이견이면 「분석 결과」 배지 + 적용 버튼 (규약 ④⑤) */}
            {renderWorkCategoryRow(
              `grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] ${rowBorder}`,
              labelCls,
              valueCls,
            )}

            {/* 위치 — companyId/siteId 있으면 상위 PhotoMetaSection에서 표시하므로 숨김 */}
            {!companyId && !siteId && ((location && (location.area || location.detail)) || canEdit) && (
              <div className={`grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] ${rowBorder}`}>
                <div className={labelCls}>위치</div>
                <div className={`${valueCls} text-xs text-ink-500`}>{renderLocationContent()}</div>
              </div>
            )}

            {/* 유해위험요인 (설명) - full-width value */}
            <div className={rowBorder}>
              <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr]">
                <div className={`${labelCls} sm:row-span-1`}>유해위험요인</div>
                <div className={`${valueCls} hidden sm:block`}>{renderDescriptionContent()}</div>
              </div>
              <div className="sm:hidden px-2.5 py-1.5">{renderDescriptionContent()}</div>
            </div>

            {/* 위험성 평가 편집 (riskAssessment 편집 가능 시) */}
            {riskAssessment && canEdit && (
              <div className={rowBorder}>
                <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr]">
                  <div className={labelCls}>위험성평가</div>
                  <div className={valueCls}>{renderRiskAssessment()}</div>
                </div>
              </div>
            )}

            {/* 개선대책 - full-width value (우수사례는 숨김) */}
            {!isBestPractice && (
            <div className={rowBorder}>
              {/* Desktop: 2열 그리드 */}
              <div className="hidden sm:grid grid-cols-[6rem_1fr]">
                <div className={labelBase}>
                  개선대책
                  {measuresMod && <div className="text-[10px] text-amber-600 mt-0.5">수정됨</div>}
                </div>
                <div className={valueCls}>{renderMeasures(true)}</div>
              </div>
              {/* Mobile: 1열 (라벨 전체폭 + 값 전체폭) */}
              <div className="sm:hidden">
                <div className="bg-ink-100 px-2.5 py-1 font-medium text-ink-600 text-xs border-b border-line">
                  개선대책
                  {measuresMod && <div className="text-[10px] text-amber-600 mt-0.5">수정됨</div>}
                </div>
                <div className="px-2.5 py-1.5">{renderMeasures(true)}</div>
              </div>
            </div>
            )}

            {/* 관련법규 - full-width value (우수사례는 숨김) */}
            {!isBestPractice && (
            <div className={rowBorder}>
              {/* Desktop: 2열 그리드 */}
              <div className="hidden sm:grid grid-cols-[6rem_1fr]">
                <div className={labelBase}>
                  관련법규
                  {legalBasisMod && <div className="text-[10px] text-amber-600 mt-0.5">수정됨</div>}
                </div>
                <div className={valueCls}>{renderLegalBasisItem(true)}</div>
              </div>
              {/* Mobile: 1열 (라벨 전체폭 + 값 전체폭) */}
              <div className="sm:hidden">
                <div className="bg-ink-100 px-2.5 py-1 font-medium text-ink-600 text-xs border-b border-line">
                  관련법규
                  {legalBasisMod && <div className="text-[10px] text-amber-600 mt-0.5">수정됨</div>}
                </div>
                <div className="px-2.5 py-1.5">{renderLegalBasisItem(true)}</div>
              </div>
            </div>
            )}

            {/* 조치현황 (해소됨일 때만) */}
            {hazardResolutionEnabled && (finding.resolutionStatus === 'COMPLIED' || (editable && !hasReport && !isDeleted && onResolveHazard)) && (
              <div className={rowBorder}>
                <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr]">
                  <div className={labelCls}>조치현황</div>
                  <div className={valueCls}>
                    {finding.resolutionStatus === 'COMPLIED' ? (
                      <div className="space-y-1.5">
                        {(() => {
                          const resPhotos = finding.resolutionPhotos?.length
                            ? finding.resolutionPhotos
                            : finding.resolutionPhotoUrl
                              ? [{ url: finding.resolutionPhotoUrl }]
                              : [];
                          return resPhotos.length > 0 ? (
                            <div>
                              <span className="text-xs font-medium text-ink-600">조치사진:</span>
                              <div className="flex gap-1.5 mt-1 flex-wrap">
                                {resPhotos.map((p, i) => (
                                  <img key={i} src={normalizeFileUrl(p.url)} alt={`조치사진 ${i + 1}`} className="w-16 h-16 object-cover rounded-card-sm border border-line" />
                                ))}
                              </div>
                            </div>
                          ) : null;
                        })()}
                        {finding.notes && (
                          <div>
                            <span className="text-xs font-medium text-ink-600">조치사항:</span>
                            <p className="text-sm text-ink-700 mt-0.5 whitespace-pre-wrap">{finding.notes}</p>
                          </div>
                        )}
                        {editable && !hasReport && onResolveHazard && (
                          <TextButton data-action-button onClick={(e) => { e.stopPropagation(); onResolveHazard(finding.id); }}
                            className="text-xs font-medium">수정</TextButton>
                        )}
                      </div>
                    ) : (
                      editable && !hasReport && !isDeleted && onResolveHazard && (
                        <button type="button" data-action-button onClick={(e) => { e.stopPropagation(); onResolveHazard(finding.id); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-control hover:bg-amber-100 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-8-8h16" />
                          </svg>
                          위험요인 해소
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {renderConfirmDialogs()}
      </div>
    );
  }

  // =====================================================================
  // 기존 카드 레이아웃 (tableMode=false)
  // =====================================================================

  // ── 우수사례 카드 레이아웃 ──
  if (isBestPractice) {
    return (
      <div className={`${findingIndex > 0 ? 'pt-2 border-t border-emerald-100' : ''} ${isDeleted ? 'opacity-60' : ''} ${bpExcludedFromReport ? 'opacity-60' : ''}`}>
        {renderBanners()}
        {renderConfirmDialogs()}

        {/* 보고서 포함 안내 - 첫 항목 위에만 */}
        {findingIndex === 0 && findingSelectionEnabled && !hasReport && onToggleMeasureSelection && (
          <div className="rounded-card-sm bg-amber-50 ring-1 ring-amber-200 px-2.5 py-1.5 text-xs text-amber-600 mb-2">
            보고서에 포함할 우수사례를 선택해주세요 (미선택 시 보고서에서 제외됩니다)
          </div>
        )}

        {/* 헤더: 체크박스 + 우수 뱃지 + 분류 */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {findingSelectionEnabled && !hasReport && onToggleMeasureSelection && (
            <input
              type="checkbox"
              checked={bpIncluded}
              onChange={(e) => onToggleMeasureSelection(BP_INCLUDED_MARKER, e.target.checked)}
              className="w-3.5 h-3.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
          )}
          {findingSelectionEnabled && hasReport && (
            bpIncluded
              ? <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              : <svg className="w-3.5 h-3.5 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" /></svg>
          )}
          {globalFindingNumber !== undefined && (
            <Badge tone="slate" shape="rounded" className="justify-center min-w-[22px] tabular-nums">
              #{globalFindingNumber}
            </Badge>
          )}
          <SeverityBadge severity="GOOD" size="sm" />
          <FieldHighlight findingId={finding.id} field="category">
            <ModifiedFieldHighlight modification={combinedCategoryMod} editable={!!canEdit} onRevert={categoryRevertHandler} formatOriginal={categoryFormatOriginal}>
              {renderCategoryContent()}
            </ModifiedFieldHighlight>
          </FieldHighlight>
          {!compact && totalCount > 1 && (
            <span className="text-xs text-ink-400 ml-auto">({findingIndex + 1}/{totalCount})</span>
          )}
          {renderDeleteButton()}
          {renderCollapseButton()}
        </div>

        {(!collapsible || isExpanded) && (
          <>
            {/* 내용 */}
            <div className="border border-line rounded-card p-3 mb-3">
              {renderDescriptionContent()}
            </div>

            {/* 우수 포인트 */}
            {recommendation?.measures && recommendation.measures.length > 0 && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-card p-3 mb-3">
                <div className="text-xs font-medium text-emerald-700 mb-1.5 flex items-center gap-1">
                  <AwardIcon className="h-3.5 w-3.5" /> 우수 포인트
                </div>
                <ul className="text-sm text-ink-700 space-y-1 ml-4">
                  {recommendation.measures.map((m, i) => (
                    <li key={i} className="list-disc list-outside">{m}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* AI 신뢰도 */}
            {finding.aiGenerated && finding.aiConfidence !== undefined && (
              <div className="flex items-center gap-2 text-xs text-ink-400">
                <span>분석 신뢰도:</span>
                <span className="flex items-center gap-1">
                  <span className={`font-medium ${getConfidenceStyle(finding.aiConfidence)}`}>
                    {Math.round(finding.aiConfidence * 100)}%
                  </span>
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── 기존 위험요인 카드 레이아웃 ──
  const cardAllExcluded = findingSelectionEnabled && (!selectedMeasures || selectedMeasures.length === 0);
  const cardExcludedFromReport = hasReport && cardAllExcluded;
  return (
    <div
      className={`
        ${findingIndex > 0 ? 'pt-2 border-t border-line-soft' : ''}
        ${isDeleted ? 'opacity-60' : ''}
        ${hasBoundingBox && onClick ? 'cursor-pointer hover:bg-surface-hover -mx-2 px-2 py-1 rounded transition-colors' : ''}
        ${isSelected ? 'bg-brand-50 ring-1 ring-brand-200 -mx-2 px-2 py-1 rounded' : ''}
        ${cardExcludedFromReport ? 'opacity-60 text-ink-400' : cardAllExcluded ? 'text-ink-400' : ''}
      `}
      onClick={handleClick}
    >
      {renderBanners()}

      {cardExcludedFromReport && (
        <div className="bg-ink-100 rounded-card-sm px-2.5 py-1.5 text-xs text-ink-500 flex items-center gap-1 mb-2">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" /></svg>
          보고서에서 제외된 발견사항
        </div>
      )}

      {renderConfirmDialogs()}

      {/* 헤더: 심각도 + 카테고리 + 번호 */}
      <FieldHighlight findingId={finding.id} field="category" className={(!collapsible || isExpanded) ? 'mb-2' : ''}>
        <ModifiedFieldHighlight
          modification={combinedCategoryMod}
          editable={!!canEdit}
          onRevert={categoryRevertHandler}
          formatOriginal={categoryFormatOriginal}
        >
          <div className="flex items-center gap-2 flex-wrap">
            {globalFindingNumber !== undefined && (
              <Badge tone="slate" shape="rounded" className="justify-center min-w-[22px] tabular-nums">
                #{globalFindingNumber}
              </Badge>
            )}
            <SeverityBadge
              likelihoodScore={riskAssessment?.likelihood ?? finding.aiLikelihoodScore}
              severityScore={riskAssessment?.severity ?? finding.aiSeverityScore}
              riskLevelScore={riskAssessment?.riskLevel ?? undefined}
              config={riskConfig}
              size="sm"
            />
            {renderCategoryContent()}
            {hazardResolutionEnabled && finding.resolutionStatus === 'COMPLIED' && (
              <Badge tone="emerald" shape="rounded" size="sm">해소됨</Badge>
            )}
            {!compact && totalCount > 1 && (
              <span className="text-xs text-ink-400 ml-auto">({findingIndex + 1}/{totalCount})</span>
            )}
            {renderDeleteButton()}
            {renderHazardRevertButton()}
            {renderCollapseButton()}
          </div>
        </ModifiedFieldHighlight>
      </FieldHighlight>

      {/* 상세 내용 - collapsible이면 isExpanded일 때만 표시 */}
      {(!collapsible || isExpanded) && (
        <>
      {/* PC: 2컬럼 카드 레이아웃, Mobile: 1컬럼 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
      <div className="border border-line rounded-card p-3">
      {/* 위치 정보 */}
      {((location && (location.area || location.detail)) || canEdit) && (
        <FieldHighlight findingId={finding.id} field="location" className="mb-2">
          <ModifiedFieldHighlight
            modification={findModification(aiModifications?.modifiedFields, 'location')}
            editable={!!canEdit}
            onRevert={canEdit ? async () => {
              const mod = findModification(aiModifications?.modifiedFields, 'location');
              if (mod && editHandlers) {
                const original = mod.originalValue as { area?: string; detail?: string };
                await editHandlers.onUpdateFinding(finding.id, { location: original }, { isRevert: true });
              }
            } : undefined}
          >
            <div className="text-xs text-ink-500 flex items-center gap-1">
              <MapPinIcon className="h-3.5 w-3.5 flex-none" />
              {canEdit ? (
                <EditableField
                  value={[location?.area, location?.detail].filter(Boolean).join(' - ')}
                  onSave={async (value) => {
                    const parts = value.split('-').map(s => s.trim());
                    await editHandlers!.onUpdateFinding(finding.id, {
                      location: { area: parts[0] || undefined, detail: parts[1] || undefined },
                    });
                  }}
                  placeholder="위치 정보 추가"
                />
              ) : (
                <span>{location?.area}{location?.area && location?.detail && ' - '}{location?.detail}</span>
              )}
            </div>
          </ModifiedFieldHighlight>
        </FieldHighlight>
      )}

      {/* 설명 */}
      <FieldHighlight findingId={finding.id} field="description" className="mb-3">
        <ModifiedFieldHighlight
          modification={findModification(aiModifications?.modifiedFields, 'description')}
          editable={!!canEdit}
          onRevert={canEdit ? async () => {
            const mod = findModification(aiModifications?.modifiedFields, 'description');
            if (mod && editHandlers) {
              const original = mod.originalValue as string;
              await editHandlers.onUpdateFinding(finding.id, { description: original }, { isRevert: true });
            }
          } : undefined}
        >
          {canEdit ? (
            <EditableField
              value={finding.description}
              onSave={async (value) => { await editHandlers!.onUpdateFinding(finding.id, { description: value }); }}
              type="textarea"
              className="text-sm text-ink-700 leading-relaxed"
            />
          ) : (
            <p className="text-sm text-ink-700 leading-relaxed">{finding.description}</p>
          )}
        </ModifiedFieldHighlight>
      </FieldHighlight>

      {/* 위험성 평가 */}
      {riskAssessment && (
        <FieldHighlight findingId={finding.id} field="riskAssessment" className="mb-3">
          <div className="p-2 bg-surface-soft rounded-card-sm">
            <div className="text-xs font-medium text-ink-600 mb-1.5">위험성 평가</div>
            {renderRiskAssessment()}
          </div>
        </FieldHighlight>
      )}
      </div>
      {/* 오른쪽 카드: 개선대책 (우수사례는 숨김) */}
      {!isBestPractice && (
      <div className="border border-line rounded-card p-3">
      {renderMeasures()}
      </div>
      )}
      </div>
      {/* 전체 너비 카드: 관련법규 (우수사례는 숨김) */}
      {!isBestPractice && (
      <div className="border border-line rounded-card p-3 mb-3">
      {renderLegalBasisItem()}
      </div>
      )}

      {/* AI 신뢰도 */}
      {finding.aiGenerated && finding.aiConfidence !== undefined && (
        <div className="flex items-center gap-2 text-xs text-ink-400">
          <span>분석 신뢰도:</span>
          <span className="flex items-center gap-1">
            <span className={`font-medium ${getConfidenceStyle(finding.aiConfidence)}`}>
              {Math.round(finding.aiConfidence * 100)}%
            </span>
          </span>
        </div>
      )}

      {/* 위험요인 해소 */}
      {renderResolution()}

        </>
      )}
    </div>
  );
}

/**
 * 개선대책 목록 (변경사항 표시 포함)
 */
function MeasuresWithChanges({
  currentMeasures,
  measureItems,
  modification,
  hkMeasureCount,
  editable = false,
  onRevert,
  onAdd,
  onRemove,
  hideLabel = false,
  findingSelectionEnabled = false,
  selectedMeasures,
  onToggleMeasureSelection,
  hasReport = false,
  currentUserId,
  isMyCompleted = false,
  isPrimary = false,
  isOwner = false,
  isDeleted = false,
}: {
  currentMeasures: string[];
  measureItems?: Array<{ text: string; isPrimary?: boolean; source?: 'ai' | 'hk'; score?: number; reason?: string }>;
  modification?: { originalValue: unknown; currentValue: unknown };
  hkMeasureCount?: number;
  editable?: boolean;
  onRevert?: () => Promise<void>;
  onAdd?: (measure: string) => Promise<void>;
  onRemove?: (index: number) => Promise<void>;
  hideLabel?: boolean;
  findingSelectionEnabled?: boolean;
  selectedMeasures?: SelectedMeasureEntry[];
  onToggleMeasureSelection?: (measure: string, selected: boolean) => void;
  hasReport?: boolean;
  currentUserId?: string;
  isMyCompleted?: boolean;
  isPrimary?: boolean;
  isOwner?: boolean;
  /**
   * 이 발견사항이 **삭제분**인가 — 아래 `showSelectionGuide` 하나가 읽는다(가드가 아니라 사실이다).
   *
   * ⚠️ **읽히는 곳은 `!modification` 읽기 전용 갈래 하나다.** 편집 갈래
   * (`editable && onAdd && onRemove` → `EditableMeasureList`)에는 **삭제분이 구조적으로
   * 도달하지 않으므로**(`canEdit = editable && editHandlers && !isDeleted`) 그쪽으로 넘기지
   * 않았다 — 영영 발동하지 않는 가드는 **거짓 안심**이 된다. 호출부 둘이 똑같이 넘기는 것은
   * 그 사실과 무관하게 **값이 참이기 때문**이다.
   */
  isDeleted?: boolean;
}) {
  // 수정 정보가 없으면 편집 가능한 목록 표시
  if (!modification) {
    if (currentMeasures.length === 0 && !editable) return null;

    if (editable && onAdd && onRemove) {
      return <EditableMeasureList measures={currentMeasures} measureItems={measureItems} onAdd={onAdd} onRemove={onRemove} hkMeasureCount={hkMeasureCount} hideLabel={hideLabel} findingSelectionEnabled={findingSelectionEnabled} selectedMeasures={selectedMeasures} onToggleMeasureSelection={onToggleMeasureSelection} hasReport={hasReport} currentUserId={currentUserId} isMyCompleted={isMyCompleted} isPrimary={isPrimary} isOwner={isOwner} />;
    }

    const canToggle = findingSelectionEnabled && !hasReport && !!onToggleMeasureSelection;
    // ⚠️ `!isDeleted` — 삭제된 발견사항은 보고서에 애초에 들어가지 않으므로
    // 「개선대책을 선택해주세요」가 **할 일 없는 안내**다(2026-10-08 P4-y 실측: 선택 3/3인데
    // 그 안내가 삭제분 2카드에 떠 있었다). 탐지기: `__tests__/FindingDetail.deletedGuide.test.tsx`
    //
    // ⚠️ **`canToggle`이 아니라 안내 축에만 둔다** — `canToggle`에 넣으면
    // `showLockedSelection`(잠긴 선택 표시)이 삭제분에서 **새로 켜진다**.
    // ⚠️ **그리고 이 자리가 그 축의 «유일한» 소유자다** — 편집 갈래
    // (`EditableMeasureList`)는 삭제분이 **구조적으로 도달하지 않는다**:
    // `canEdit = editable && editHandlers && !isDeleted`(`:355`)라 삭제분은 언제나 이 읽기
    // 전용 갈래로 온다. 그래서 그쪽에 같은 가드를 두지 않았다(영영 발동하지 않는 가드는
    // 비용만이 아니라 **거짓 안심**이다 — 다음 사람이 「거기서 막는다」를 근거로 쓴다).
    const showSelectionGuide = canToggle && currentMeasures.length > 0 && !isDeleted;
    const hasNoSelection = showSelectionGuide && (!selectedMeasures || selectedMeasures.length === 0);
    const showReportIndicator = findingSelectionEnabled && hasReport && selectedMeasures && selectedMeasures.length > 0;
    const showLockedSelection = findingSelectionEnabled && !hasReport && !onToggleMeasureSelection && selectedMeasures && selectedMeasures.length > 0;

    return (
      <div className={showSelectionGuide ? `rounded-card-sm p-2 ${hasNoSelection ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-brand-50/30 ring-1 ring-brand-200'}` : ''}>
        {!hideLabel && (
          <div className="text-xs font-medium text-ink-600 mb-1.5 flex items-center gap-1">
            <WrenchIcon className="h-3.5 w-3.5" /> 개선 대책
          </div>
        )}
        {showSelectionGuide && (
          <p className={`text-xs mb-1.5 ${hasNoSelection ? 'text-amber-600 font-medium' : 'text-brand-500'}`}>
            {hasNoSelection
              ? '보고서에 포함할 개선대책을 선택해주세요 (미선택 시 이 위험요인은 보고서에서 제외됩니다)'
              : `${selectedMeasures!.length}개 선택됨`}
          </p>
        )}
        <ul className="text-sm text-ink-700 space-y-1 ml-4">
          {currentMeasures.map((measure, idx) => {
            const selectedEntry = selectedMeasures?.find((x) => x.measure === measure);
            const isSelected = !!selectedEntry;
            const showCheckbox = canToggle;
            const isPrimaryMeasure = measureItems?.some((m) => m.text === measure && m.isPrimary);
            const isExcludedFromReport = showReportIndicator && !isSelected;
            const isLockedUnselected = showLockedSelection && !isSelected;
            const isMySelection = isSelected && !!currentUserId && selectedEntry?.selectedById === currentUserId;
            const canOverride = isPrimary || isOwner;
            const selectorCompleted = selectedEntry?.selectedByCompletionStatus === 'COMPLETED';
            const selectorName = selectedEntry?.selectedByName ?? '다른 참여자';
            let checkboxDisabledReason: string | null = null;
            if (isSelected && !isMySelection && !canOverride) {
              checkboxDisabledReason = `${selectorName}님이 선택한 항목입니다`;
            } else if (isSelected && isMySelection && isMyCompleted) {
              checkboxDisabledReason = '완료 서명 후에는 해제할 수 없습니다. 먼저 서명을 재설정해주세요.';
            } else if (isSelected && !isMySelection && canOverride && selectorCompleted) {
              checkboxDisabledReason = `${selectorName}님이 완료 서명한 항목입니다. 먼저 해당 참여자의 서명을 재설정해주세요.`;
            }
            const isCheckboxDisabled = checkboxDisabledReason !== null;
            return (
              <React.Fragment key={idx}>
                {hkMeasureCount != null && hkMeasureCount > 0 && idx === hkMeasureCount && (
                  <li className="list-none -ml-4 my-1.5"><hr className="border-line" /></li>
                )}
                <li className={`flex items-start gap-2 ${showCheckbox ? 'list-none' : 'list-disc list-outside'} ${isExcludedFromReport || isLockedUnselected ? 'text-ink-400' : ''}`}>
                  {showCheckbox ? (
                    <label
                      className={`flex items-start gap-2 flex-1 ${isCheckboxDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      title={checkboxDisabledReason ?? undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isCheckboxDisabled}
                        onChange={() => onToggleMeasureSelection?.(measure, !isSelected)}
                        className={`w-3.5 h-3.5 mt-0.5 rounded border-ink-300 text-brand-600 focus:ring-brand-500 flex-shrink-0 ${
                          isCheckboxDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                      />
                      <span className="min-w-0">
                        {measure}
                        {isPrimaryMeasure && (
                          <span title="AI가 가장 시급하다고 판단한 즉각 조치">
                            <Badge tone="brand" size="sm" className="ml-2">
                              우선
                            </Badge>
                          </span>
                        )}
                        {isSelected && selectedEntry?.selectedByName && !isMySelection && (
                          <Badge tone="slate" size="sm" className="ml-2">
                            {selectedEntry.selectedByName} 선택
                          </Badge>
                        )}
                      </span>
                    </label>
                  ) : (
                    <span>
                      {measure}
                      {isPrimaryMeasure && (
                        <span title="AI가 가장 시급하다고 판단한 즉각 조치">
                          <Badge tone="brand" size="sm" className="ml-2">
                            우선
                          </Badge>
                        </span>
                      )}
                      {(showReportIndicator || showLockedSelection) && isSelected && (
                        <Badge tone="brand" size="sm" className="ml-2">
                          보고서 포함
                        </Badge>
                      )}
                    </span>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </div>
    );
  }

  // 변경사항 계산
  const originalMeasures = (modification.originalValue as string[]) || [];
  const changes = calculateArrayChanges(originalMeasures, currentMeasures);

  // 변경된 내용이 없으면 일반 표시
  const hasChanges = changes.some(c => c.status !== 'unchanged');
  if (!hasChanges && currentMeasures.length === 0 && !editable) return null;

  // 편집 가능하고 변경사항이 있으면 EditableMeasureList + 되돌리기 버튼
  if (editable && onAdd && onRemove) {
    return (
      <ModifiedFieldHighlight
        modification={{ field: 'measures', originalValue: modification.originalValue, currentValue: modification.currentValue }}
        editable={editable}
        onRevert={onRevert}
        showOriginalInline={false}
      >
        <EditableMeasureList
          measures={currentMeasures}
          measureItems={measureItems}
          onAdd={onAdd}
          onRemove={onRemove}
          changes={changes}
          hkMeasureCount={hkMeasureCount}
          hideLabel={hideLabel}
          findingSelectionEnabled={findingSelectionEnabled}
          selectedMeasures={selectedMeasures}
          onToggleMeasureSelection={onToggleMeasureSelection}
          hasReport={hasReport}
          currentUserId={currentUserId}
          isMyCompleted={isMyCompleted}
          isPrimary={isPrimary}
          isOwner={isOwner}
        />
      </ModifiedFieldHighlight>
    );
  }

  return (
    <div className={hasChanges ? 'relative' : ''}>
      {/* 변경된 경우 배경 하이라이트 */}
      {hasChanges && (
        <div className="absolute inset-0 -m-0.5 bg-amber-50 border border-amber-200 rounded pointer-events-none" />
      )}
      <div className={hasChanges ? 'relative' : ''}>
        {!hideLabel && (
          <div className="text-xs font-medium text-ink-600 mb-1.5">
            <WrenchIcon className="h-3.5 w-3.5" /> 개선 대책
            {hasChanges && (
              <div className="text-[10px] text-amber-600 font-normal mt-0.5">수정됨</div>
            )}
          </div>
        )}
        <ul className="text-sm space-y-1 ml-4">
          {changes.map((item, idx) => (
            <React.Fragment key={idx}>
              {hkMeasureCount != null && hkMeasureCount > 0 && idx === hkMeasureCount && (
                <li className="list-none -ml-4 my-1.5"><hr className="border-line" /></li>
              )}
              <li
                className={`list-disc list-outside ${
                  item.status === 'removed'
                    ? 'text-red-400 line-through'
                    : item.status === 'added'
                    ? 'text-ink-700'
                    : 'text-ink-700'
                }`}
              >
                {item.value}
                {item.status !== 'unchanged' && (
                  <ChangeStatusBadge status={item.status} />
                )}
              </li>
            </React.Fragment>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * 관련법규 목록 (변경사항 표시 포함)
 */
function LegalBasisItemWithChanges({
  currentLegalBasis,
  modification,
  editable = false,
  onRevert,
  onAdd,
  onRemove,
  lawSearchStatus,
  category,
  subCategory,
  measures,
  hideLabel = false,
}: {
  currentLegalBasis: LegalBasisItem[];
  modification?: { originalValue: unknown; currentValue: unknown };
  editable?: boolean;
  onRevert?: () => Promise<void>;
  onAdd?: (legalBasis: LegalBasisItem, selectedMeasures?: string[]) => Promise<void>;
  onRemove?: (index: number) => Promise<void>;
  lawSearchStatus?: LawSearchStatus;
  category?: string;
  subCategory?: string | null;
  measures?: string[];
  hideLabel?: boolean;
}) {
  // 법령 검색 중이면 로딩 인디케이터 표시
  const isSearching = lawSearchStatus === LawSearchStatus.SEARCHING;

  // 수정 정보가 없으면 편집 가능한 목록 표시
  if (!modification) {
    if (currentLegalBasis.length === 0 && !editable && !isSearching) return null;

    if (editable && onAdd && onRemove) {
      return (
        <div>
          <EditableLegalList legalBasis={currentLegalBasis} onAdd={onAdd} onRemove={onRemove} category={category} subCategory={subCategory} measures={measures} hideLabel={hideLabel} />
          {isSearching && <LawSearchLoadingIndicator />}
        </div>
      );
    }

    return (
      <div>
        {!hideLabel && (
          <div className="text-xs font-medium text-ink-600 mb-1.5">
            관련 법규
            {isSearching && (
              <span className="ml-1 inline-flex items-center gap-1 text-brand-600">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-[10px]">검토 중...</span>
              </span>
            )}
          </div>
        )}
        {hideLabel && isSearching && (
          <div className="flex items-center gap-1 text-brand-600 text-xs mb-1">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-[10px]">검색 중...</span>
          </div>
        )}
        {currentLegalBasis.length > 0 ? (
          <GroupedLegalBasisCardView items={currentLegalBasis.map(law => ({ law }))} />
        ) : isSearching ? (
          <LawSearchLoadingIndicator />
        ) : null}
      </div>
    );
  }

  // 변경사항 계산
  const originalLegalBasis = (modification.originalValue as LegalBasisItem[]) || [];
  const changes = calculateArrayChanges(
    originalLegalBasis,
    currentLegalBasis,
    (a, b) => a.type === b.type && a.article === b.article && a.summary === b.summary
  );

  // 변경된 내용이 없으면 일반 표시
  const hasChanges = changes.some(c => c.status !== 'unchanged');
  if (!hasChanges && currentLegalBasis.length === 0 && !editable) return null;

  // 편집 가능하고 변경사항이 있으면 EditableLegalList + 되돌리기 버튼
  if (editable && onAdd && onRemove) {
    return (
      <ModifiedFieldHighlight
        modification={{ field: 'legalBasis', originalValue: modification.originalValue, currentValue: modification.currentValue }}
        editable={editable}
        onRevert={onRevert}
        showOriginalInline={false}
      >
        <EditableLegalList
          legalBasis={currentLegalBasis}
          onAdd={onAdd}
          onRemove={onRemove}
          changes={changes}
          category={category}
          subCategory={subCategory}
          measures={measures}
          hideLabel={hideLabel}
                 />
      </ModifiedFieldHighlight>
    );
  }

  return (
    <div className={hasChanges ? 'relative' : ''}>
      {/* 변경된 경우 배경 하이라이트 */}
      {hasChanges && (
        <div className="absolute inset-0 -m-0.5 bg-amber-50 border border-amber-200 rounded pointer-events-none" />
      )}
      <div className={hasChanges ? 'relative' : ''}>
        {!hideLabel && (
          <div className="text-xs font-medium text-ink-600 mb-1.5">
            관련 법규
            {hasChanges && (
              <div className="text-[10px] text-amber-600 font-normal mt-0.5">수정됨</div>
            )}
          </div>
        )}
        <GroupedLegalBasisCardView items={changes.map(c => ({ law: c.value, status: c.status }))} />
      </div>
    </div>
  );
}

/**
 * 법령 검토 중 로딩 인디케이터.
 * 임베딩 매칭 + AI judge 적합도 검증 단계가 통합 진행되므로 "검토 중"으로 표현 (2026-04-29).
 */
function LawSearchLoadingIndicator() {
  return (
    <div className="flex items-center gap-2 p-2 bg-brand-50 border border-brand-100 rounded-card-sm text-xs text-brand-600">
      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>관련 법령을 검토하고 있습니다...</span>
    </div>
  );
}

export default FindingDetail;
